import type {
  ReportPrintItemOption,
  ReportPrintOptions,
  ReportResult,
} from '../types/reports';

/** The document every report prints until someone saves a layout. */
export const DEFAULT_REPORT_PRINT_OPTIONS: ReportPrintOptions = {
  summary: true,
  breakdowns: true,
  details: true,
  summaryItems: [],
  breakdownItems: [],
  breakdownItemOptions: [],
};

/**
 * Key for one row inside a breakdown. Composite, because a row label ("Cash",
 * "COMPLETED", …) is only unique within its block.
 */
export function breakdownRowKey(breakdownTitle: string, rowLabel: string): string {
  return `${breakdownTitle}::${rowLabel}`;
}

/**
 * Merges the report's current items with the saved layout.
 *
 * Saved visibility and custom labels are kept for matching keys; an item the
 * report emits but the layout has never seen prints normally (so a newly added
 * metric is never silently hidden). Order always follows the report.
 */
export function mergePrintItemOptions(
  keys: string[],
  saved: ReportPrintItemOption[],
): ReportPrintItemOption[] {
  const byKey = new Map(saved.map((item) => [item.key, item]));
  return keys.map(
    (key) => byKey.get(key) ?? { key, label: '', visible: true },
  );
}

/** The layout to show in the modal: saved preferences plus the current items. */
export function buildPrintOptions(
  base: ReportPrintOptions,
  result?: ReportResult,
): ReportPrintOptions {
  if (!result) return base;

  const breakdownRows = result.breakdowns.flatMap((breakdown) =>
    breakdown.items.map((item) => breakdownRowKey(breakdown.title, item.label)),
  );

  return {
    ...base,
    summaryItems: mergePrintItemOptions(
      result.summary.map((item) => item.label),
      base.summaryItems,
    ),
    breakdownItems: mergePrintItemOptions(
      result.breakdowns.map((breakdown) => breakdown.title),
      base.breakdownItems,
    ),
    breakdownItemOptions: mergePrintItemOptions(
      breakdownRows,
      base.breakdownItemOptions,
    ),
  };
}

/** `false` when the saved layout would render an almost-empty document. */
export function hasPrintableSection(
  options: ReportPrintOptions,
  printWithoutDetails?: boolean,
): boolean {
  return (
    options.summary ||
    options.breakdowns ||
    (options.details && !printWithoutDetails)
  );
}

/** Number of unchecked items, shown as a badge on the "Print options" button. */
export function countHiddenPrintItems(options: ReportPrintOptions): number {
  return [
    ...options.summaryItems,
    ...options.breakdownItems,
    ...options.breakdownItemOptions,
  ].filter((item) => !item.visible).length;
}
